// Iter-251/253 — Bank statement reconciliation panel (top of Monthly Statement).
import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Landmark, Upload, Loader2, ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, Trash2, History, Scale, HandCoins, ExternalLink, Link2, RefreshCw,
} from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const fmtMad = (n) =>
  (Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtYm = (ym) => `${String(ym).slice(5, 7)}/${String(ym).slice(0, 4)}`;
const salaryPartialRefused = (ops, amount) => {
  const sum = ops.reduce((s, o) => s + (Number(o.amount_mad) || 0), 0);
  return ops.some((o) => o.kind === "liability_salary") && (Number(amount) || 0) < sum - Math.max(1, 0.005 * sum);
};

const KIND_FR = {
  cabinet_expense: "Dépense cabinet", freelance_invoice: "Facture freelance", ndf_note: "Note de frais",
  salary: "Salaire du mois", liability_salary: "Salaire impayé", liability_charges: "Charges sociales",
  invoice_payment: "Règlement facture", declaration: "Déclaration",
};

// Iter-312 — plain-language explanation of why an app operation has no bank line.
const orphanHint = (op, opYm, curYm, nUnmatchedDebits) => {
  const canMatch = nUnmatchedDebits > 0
    ? "S'il a été payé sur ce relevé, cliquez sur l'icône Rapprocher."
    : "Toutes les lignes de ce relevé sont déjà rapprochées : il n'a donc pas été payé ce mois-ci.";
  if (op.kind === "liability_salary" && opYm && opYm < curYm) {
    return `Salaire de ${fmtYm(opYm)} toujours dû (${fmtMad(op.amount_mad)} MAD) : aucun virement de ce relevé ne le règle. Il reste dans « Dette salaires » jusqu'à son paiement. ${canMatch}`;
  }
  if (op.kind === "liability_salary" || op.salary_variants) {
    return `Salaire de ${fmtYm(curYm)} non trouvé sur le relevé : non comptabilisé ce mois, il passe en « Dette salaires ». ${canMatch}`;
  }
  if (op.kind === "liability_charges") return "Charges sociales estimées (coût employeur − net) non couvertes par les paiements CNSS / DGI (IR) rapprochés. Les paiements CNSS / DAMANCOM et DGI (IR) sont imputés automatiquement. Si vous êtes à jour, ce reste vient de l'écart entre l'estimation et le montant réellement payé : cliquez « Déclarer à jour ».";
  if (op.direction === "IN") return "Règlement enregistré dans l'application sans crédit correspondant sur ce relevé (encaissé sur un autre compte ou un autre mois ?).";
  return "Saisie dans l'application, mais aucun débit correspondant sur ce relevé (payée autrement, par un autre compte ou un autre mois). Elle explique une partie du delta des débits.";
};

// Iter-312 — which salary month(s) a matched bank line pays.
const salaryMonths = (match, curYm) => [...new Set((match?.op_ids || []).map((o) => {
  const p = String(o).split(":");
  if (p[0] === "salary") return curYm;
  if (p[0] === "liability" && p[1] === "salary") return p[3];
  return null;
}).filter(Boolean))].sort();

const MONTH_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const DeltaChip = ({ label, statement, book, delta, testid, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    title="Cliquer pour voir le détail des éléments qui composent ce delta"
    className="px-3 py-2 rounded-md border border-zinc-200 bg-white text-left w-full hover:border-zinc-400 hover:bg-zinc-50/60 transition-colors cursor-pointer"
  >
    <div className="text-[10px] uppercase font-mono tracking-wider text-zinc-500">{label}</div>
    <div className="text-sm font-mono mt-0.5">
      Relevé <span className="font-semibold">{fmtMad(statement)}</span> · Comptabilisé <span className="font-semibold">{fmtMad(book)}</span>
    </div>
    <div className={`text-xs font-mono font-semibold mt-0.5 ${Math.abs(delta) < 0.01 ? "text-emerald-700" : "text-amber-700"}`}>
      Delta : {delta > 0 ? "+" : ""}{fmtMad(delta)} MAD <span className="font-normal text-zinc-400 normal-case">— détail →</span>
    </div>
  </button>
);

// Iter-265 — clicking a delta chip opens the breakdown of every element that
// builds the gap: match écarts, unmatched bank lines, unmatched app operations.
const DeltaBreakdownDialog = ({ side, recon, onClose }) => {
  if (!side || !recon) return null;
  const isDebit = side === "debit";
  const sideLines = (recon.lines || []).filter((l) => (isDebit ? l.debit : l.credit));
  const amt = (l) => Number((isDebit ? l.debit : l.credit) || 0);
  const ecarts = sideLines.filter((l) => l.match?.status === "matched" && Math.abs(l.match?.delta || 0) >= 0.01);
  const unmatched = sideLines.filter((l) => (l.match?.status || "unmatched") === "unmatched");
  // Salary orphans exist in several amount variants — keep the employer-cost
  // one (the amount actually booked in the statement) per employee.
  const salaryBest = {};
  (recon.orphan_ops || []).forEach((o) => {
    if (o.kind !== "salary") return;
    const uid = (o.op_id || "").split(":")[1];
    if (!salaryBest[uid] || String(o.op_id).endsWith("cost_employer_mad")) salaryBest[uid] = o.op_id;
  });
  const orphans = (recon.orphan_ops || []).filter((o) => {
    if (o.direction !== (isDebit ? "OUT" : "IN")) return false;
    return o.kind !== "salary";
  });
  // Iter-270 — unmatched salaries are NOT booked anymore (bank-filtered rule):
  // they no longer weigh on the delta, shown separately for information.
  const salaryOrphans = isDebit
    ? (recon.orphan_ops || []).filter((o) => o.kind === "salary" && salaryBest[(o.op_id || "").split(":")[1]] === o.op_id)
    : [];
  const sumEcarts = ecarts.reduce((s, l) => s + Number(l.match?.delta || 0), 0);
  const sumUnmatched = unmatched.reduce((s, l) => s + amt(l), 0);
  const sumOrphans = orphans.reduce((s, o) => s + Number(o.amount_mad || 0), 0);
  const explained = sumEcarts + sumUnmatched - sumOrphans;
  const displayed = Number((isDebit ? recon.summary?.delta_debit : recon.summary?.delta_credit) || 0);
  const residual = displayed - explained;
  const Sign = ({ v }) => <span className={`font-mono font-semibold whitespace-nowrap shrink-0 ${v > 0 ? "text-amber-700" : v < 0 ? "text-blue-700" : "text-zinc-500"}`}>{v > 0 ? "+" : ""}{fmtMad(v)}</span>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bank-recon-delta-breakdown" className="max-w-none w-[min(62rem,95vw)] max-h-[85vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>
            Détail du delta — {isDebit ? "Débits (sorties)" : "Crédits (entrées)"} : {displayed > 0 ? "+" : ""}{fmtMad(displayed)} MAD
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-xs">
          <p className="text-zinc-500">
            Delta = Relevé − Comptabilisé. Il se décompose en : écarts sur les lignes rapprochées,
            lignes du relevé sans opération dans l'app (+), et opérations comptabilisées absentes du relevé (−).
          </p>
          {[
            {
              key: "ecarts", title: `Écarts sur lignes rapprochées (${ecarts.length})`, sum: sumEcarts,
              rows: ecarts.map((l) => ({
                left: `${l.date || "—"} · ${(l.label || "").slice(0, 70)}`,
                mid: `comptabilisé ${fmtMad(l.match?.book_amount_mad)} → relevé ${fmtMad(l.match?.real_amount_mad)}`,
                val: Number(l.match?.delta || 0),
              })),
              empty: "Aucun écart sur les correspondances.",
            },
            {
              key: "unmatched", title: `Lignes du relevé non rapprochées (${unmatched.length})`, sum: sumUnmatched,
              rows: unmatched.map((l) => ({
                left: `${l.date || "—"} · ${(l.label || "").slice(0, 70)}`,
                mid: "présent sur le relevé, absent de l'app",
                val: amt(l),
              })),
              empty: "Toutes les lignes du relevé sont rapprochées.",
            },
            {
              key: "orphans", title: `Opérations app sans transaction bancaire (${orphans.length})`, sum: -sumOrphans,
              rows: orphans.map((o) => ({
                left: `${o.date || "—"} · ${(o.party || o.description || o.ref || o.kind || "").slice(0, 70)}`,
                mid: "comptabilisé dans l'app, absent du relevé",
                val: -Number(o.amount_mad || 0),
              })),
              empty: "Toutes les opérations de l'app sont sur le relevé.",
            },
          ].map((sec) => (
            <div key={sec.key} className="border border-zinc-200 rounded-md overflow-hidden" data-testid={`delta-breakdown-${sec.key}`}>
              <div className="px-3 py-1.5 bg-zinc-50 border-b border-zinc-100 flex items-center justify-between gap-2">
                <span className="font-semibold text-zinc-800">{sec.title}</span>
                <Sign v={sec.sum} />
              </div>
              {sec.rows.length === 0 ? (
                <div className="px-3 py-2 text-zinc-400">{sec.empty}</div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {sec.rows.map((r, i) => (
                    <div key={i} className="px-3 py-1.5 flex items-baseline gap-2 min-w-0">
                      <span className="font-mono text-zinc-700 flex-1 min-w-0 truncate" title={r.left}>{r.left}</span>
                      <span className="text-zinc-400 hidden md:inline truncate min-w-0 max-w-[38%]" title={r.mid}>{r.mid}</span>
                      <Sign v={r.val} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {salaryOrphans.length > 0 && (
            <div className="border border-blue-200 bg-blue-50/40 rounded-md overflow-hidden" data-testid="delta-breakdown-salaries-excluded">
              <div className="px-3 py-1.5 bg-blue-50 border-b border-blue-100 flex items-center justify-between gap-2">
                <span className="font-semibold text-blue-900">Salaires non trouvés sur le relevé ({salaryOrphans.length}) — exclus du calcul</span>
                <span className="font-mono text-blue-700">0,00</span>
              </div>
              <div className="divide-y divide-blue-100">
                {salaryOrphans.map((o, i) => (
                  <div key={i} className="px-3 py-1.5 flex items-baseline gap-2 min-w-0">
                    <span className="font-mono text-zinc-700 flex-1 min-w-0 truncate">{o.party || o.description}</span>
                    <span className="text-zinc-400 hidden md:inline truncate min-w-0 max-w-[45%]">non comptabilisé — aucun impact sur le delta</span>
                    <span className="font-mono text-zinc-400 whitespace-nowrap shrink-0 line-through">{fmtMad(o.amount_mad)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="border border-zinc-300 rounded-md px-3 py-2 space-y-1 bg-zinc-50/60 font-mono" data-testid="delta-breakdown-synthesis">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans font-semibold">Synthèse</div>
            <div className="flex justify-between"><span>Écarts sur correspondances</span><Sign v={sumEcarts} /></div>
            <div className="flex justify-between"><span>Lignes relevé non rapprochées</span><Sign v={sumUnmatched} /></div>
            <div className="flex justify-between"><span>Opérations app non rapprochées</span><Sign v={-sumOrphans} /></div>
            <div className="flex justify-between border-t border-zinc-300 pt-1 font-semibold"><span>Total expliqué</span><Sign v={explained} /></div>
            <div className="flex justify-between font-semibold"><span>Delta affiché</span><Sign v={displayed} /></div>
            {Math.abs(residual) >= 0.01 && (
              <div className="flex justify-between text-zinc-500" data-testid="delta-breakdown-residual">
                <span>Résidu non détaillé (arrondis, variantes net/brut des salaires…)</span><Sign v={residual} />
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const KindBadge = ({ kind }) => {
  const map = {
    exact: { label: "Exact", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    delta: { label: "Écart", tone: "bg-amber-50 text-amber-700 border-amber-200" },
    fx: { label: "Devise", tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
    approx: { label: "≈ Proche", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  };
  const cfg = map[kind] || { label: kind || "—", tone: "bg-zinc-50 text-zinc-600 border-zinc-200" };
  return <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border ${cfg.tone}`}>{cfg.label}</span>;
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border ${status === "COMPLET" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-300"}`}>
    {status || "—"}
  </span>
);

const FALLBACK_CATEGORIES = ["Banque", "Loyer", "Licences logicielles", "Honoraires", "Fournitures", "Marketing", "Comptabilité", "Juridique", "Salaires", "Charges sociales", "Télécom & Internet", "Formation", "Assurances", "Autre"];

export default function BankReconPanel({ year, month, onIntegrated, onDetectedMonth, refreshKey = 0 }) {
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [recon, setRecon] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // Iter-267 — {current,total,name}
  const [integrating, setIntegrating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showMatched, setShowMatched] = useState(false);
  const [open, setOpen] = useState(true);
  const [selected, setSelected] = useState({}); // line_id -> {checked, category}
  const [adjSelected, setAdjSelected] = useState({}); // line_id -> bool
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [pickerFor, setPickerFor] = useState(null); // line_id of the row being manually matched
  const [pickerSel, setPickerSel] = useState({}); // op_id -> bool
  const [deltaDetail, setDeltaDetail] = useState(null); // Iter-265 — "debit" | "credit" | null
  const [manualSaving, setManualSaving] = useState(false);
  const [requalifying, setRequalifying] = useState(false);
  const [reqSelected, setReqSelected] = useState({}); // line_id -> bool (bulk requalify)
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/cabinet-expenses/feature-status")
      .then((r) => { if (r.data?.categories?.length) setCategories(r.data.categories); })
      .catch(() => {});
  }, []);

  const applyReconState = (data) => {
    setRecon(data);
    setSelected({});
    setReqSelected({});
    const adj = {};
    (data?.lines || []).forEach((l) => {
      if (l.match?.adjustable && !l.adjusted) adj[l.line_id] = true;
    });
    setAdjSelected(adj);
  };

  const load = async () => {
    try {
      const r = await api.get(`/admin/bank-recon?year=${year}&month=${month}`);
      const data = r.data?.recon_id ? r.data : null;
      if (data?.charges_repaired) {
        toast.info(`Relevés passés corrigés : ${data.charges_repaired} paiement(s) CNSS / DGI (IR) réimputé(s) sur la dette de charges sociales (ils étaient classés en dépense cabinet ou non rapprochés). Chaque ligne concernée porte la mention « règle automatique ».`, { duration: 9000 });
        onIntegrated && onIntegrated();
      }
      if (data?.named_month_repaired) {
        toast.info("Salaires réattribués selon le mois indiqué dans le libellé du virement (ex. « Salaire … Avril 2026 » règle le salaire impayé d'avril).", { duration: 9000 });
        onIntegrated && onIntegrated();
      }
      // Iter-303 — statement M with M-1 debits: fetch M-1 internal expenses as candidates (older recons).
      const first = `${year}-${String(month).padStart(2, "0")}-01`;
      if (data && data.prev_month_ops == null && (data.lines || []).some((l) => l.debit && l.date && String(l.date).slice(0, 10) < first)) {
        try {
          const p = await api.post(`/admin/bank-recon/${data.recon_id}/prev-month-ops`);
          data.prev_month_ops = p.data?.prev_month_ops || [];
        } catch { /* candidates simply stay limited to the month */ }
      }
      // Iter-305 — older statements: let the salary safety-net match net salaries once.
      if (data && !data.auto_fix_v2_done) {
        try {
          const rs = await api.post(`/admin/bank-recon/${data.recon_id}/rematch-salaries`);
          if (rs.data?.matched) {
            const again = await api.get(`/admin/bank-recon?year=${year}&month=${month}`);
            Object.assign(data, again.data);
            onIntegrated && onIntegrated();
          }
        } catch { /* keep the recon as is */ }
      }
      applyReconState(data);
    } catch { setRecon(null); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [year, month, refreshKey]);
  const reanalysisStatus = recon?.reanalysis?.status;
  const [reanalyzing, setReanalyzing] = useState(false);
  const reanalyze = async () => {
    if (!window.confirm("Relancer l'analyse IA du relevé avec les données actuelles (NDF, dépenses internes, salaires, règlements) ? Les rapprochements manuels, les écarts imputés et les lignes intégrées sont conservés.")) return;
    setReanalyzing(true);
    try {
      await api.post(`/admin/bank-recon/${recon.recon_id}/reanalyze`);
      toast.info("Analyse IA relancée — cela peut prendre une à deux minutes.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Réanalyse impossible");
      setReanalyzing(false);
    }
  };
  // Iter-315 — the re-analysis runs in the background: poll until it is done.
  useEffect(() => {
    if (reanalysisStatus !== "running") {
      if (reanalyzing && reanalysisStatus) {
        setReanalyzing(false);
        const ra = recon.reanalysis;
        if (reanalysisStatus === "done") {
          toast.success(`Analyse terminée : ${ra.n_matched}/${ra.n_reanalyzed} ligne(s) rapprochée(s) · ${ra.n_kept} conservée(s)`);
          onIntegrated && onIntegrated();
        } else if (reanalysisStatus === "error") toast.error(`Analyse échouée : ${ra.error || "erreur"}`);
      }
      return undefined;
    }
    setReanalyzing(true);
    const t = setTimeout(() => load(), 4000);
    return () => clearTimeout(t);
  }, [recon]); // eslint-disable-line react-hooks/exhaustive-deps


  const upload = async (fileList) => {
    // Iter-267 — multi-statement upload: each PDF is OCR'd, its month auto-
    // detected and reconciled in place. In batch mode a month that already has
    // a live reconciliation REFUSES the file; the others are still processed.
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const multi = files.length > 1;
    setUploading(true);
    const results = [];
    try {
      for (let i = 0; i < files.length; i++) {
        setUploadProgress({ current: i + 1, total: files.length, name: files[i].name });
        const fd = new FormData();
        fd.append("file", files[i]);
        try {
          const r = await api.post(
            `/admin/bank-recon/upload?year=${year}&month=${month}${multi ? "&reject_if_exists=true" : ""}`,
            fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 },
          );
          results.push({ name: files[i].name, ok: true, data: r.data });
        } catch (e) {
          results.push({ name: files[i].name, ok: false, detail: e?.response?.data?.detail || "Analyse du relevé échouée" });
        }
      }
      const oks = results.filter((r) => r.ok);
      const kos = results.filter((r) => !r.ok);
      if (!multi) {
        if (kos.length) { toast.error(kos[0].detail); return; }
        const d = oks[0].data;
        toast.success(`Relevé analysé — ${d.summary?.n_lines || 0} ligne(s) extraite(s)`);
        if (d.year !== year || d.month !== month) {
          toast.info(`Période détectée sur le relevé : ${MONTH_FR[d.month - 1]} ${d.year} — rapprochement effectué sur ce mois`, { duration: 8000 });
          onDetectedMonth?.(d.year, d.month);
        } else {
          applyReconState(d);
        }
        return;
      }
      oks.forEach((r) => toast.success(`${r.name} → rapproché sur ${MONTH_FR[r.data.month - 1]} ${r.data.year} (${r.data.summary?.n_lines || 0} lignes)`, { duration: 8000 }));
      kos.forEach((r) => toast.error(`${r.name} : ${r.detail}`, { duration: 10000 }));
      if (oks.length > 0) {
        const cur = oks.find((r) => r.data.year === year && r.data.month === month);
        if (cur) applyReconState(cur.data);
        else onDetectedMonth?.(oks[oks.length - 1].data.year, oks[oks.length - 1].data.month);
      }
    } finally {
      setUploading(false);
      setUploadProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeRecon = async () => {
    if (!recon?.recon_id) return;
    try {
      await api.delete(`/admin/bank-recon/${recon.recon_id}`);
      setRecon(null);
      toast.success("Rapprochement supprimé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression échouée");
    }
  };

  const toggleLine = (ln) => {
    setSelected((p) => {
      const cur = p[ln.line_id];
      if (cur?.checked) return { ...p, [ln.line_id]: { ...cur, checked: false } };
      return { ...p, [ln.line_id]: { checked: true, category: cur?.category || ln.suggested_category || "Autre" } };
    });
  };

  const integrate = async () => {
    const lines = Object.entries(selected)
      .filter(([, v]) => v.checked)
      .map(([line_id, v]) => ({ line_id, category: v.category }));
    if (lines.length === 0) { toast.error("Sélectionnez au moins une ligne à intégrer."); return; }
    setIntegrating(true);
    try {
      const r = await api.post(`/admin/bank-recon/${recon.recon_id}/integrate`, { lines });
      toast.success(`${r.data.count} ligne(s) ajoutée(s) aux dépenses cabinet`);
      await load();
      onIntegrated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Intégration échouée");
    } finally { setIntegrating(false); }
  };

  const applyAmounts = async () => {
    const line_ids = Object.entries(adjSelected).filter(([, v]) => v).map(([k]) => k);
    if (line_ids.length === 0) { toast.error("Sélectionnez au moins une ligne à ajuster."); return; }
    setApplying(true);
    try {
      const r = await api.post(`/admin/bank-recon/${recon.recon_id}/apply-amounts`, { line_ids });
      toast.success(`${r.data.count} montant(s) réel(s) appliqué(s) — état mensuel et marge réelle mis à jour`);
      if ((r.data.skipped || []).length > 0) toast.warning(`${r.data.skipped.length} ligne(s) ignorée(s)`);
      await load();
      onIntegrated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Application des montants échouée");
    } finally { setApplying(false); }
  };

  const openHistory = async () => {
    try {
      const r = await api.get(`/admin/bank-recon/history?year=${year}`);
      setHistory(r.data);
      setHistoryOpen(true);
    } catch (e) {
      toast.error("Historique indisponible");
    }
  };

  // Iter-307 — candidates rebuilt live from the books when the picker opens.
  const [liveCands, setLiveCands] = useState(null);
  const [salaryPicks, setSalaryPicks] = useState({}); // op_id -> bool
  const [orphanPickFor, setOrphanPickFor] = useState(null); // op_id matched from the app side
  const [orphanLine, setOrphanLine] = useState("");
  const [declaring, setDeclaring] = useState(false);
  const declareChargesUpToDate = async (amount) => {
    if (!window.confirm(`Déclarer les charges sociales à jour au ${String(month).padStart(2, "0")}/${year} ? Le reste dû (${fmtMad(amount)} MAD) sera soldé avec votre nom et la date. Annulable depuis « Composition de la dette ».`)) return;
    setDeclaring(true);
    try {
      const r = await api.post("/admin/payroll-liabilities/declare-charges", { year, month });
      toast.success(`Charges déclarées à jour — ${fmtMad(r.data.closed_mad)} MAD soldés`);
      await load();
      onIntegrated && onIntegrated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setDeclaring(false);
    }
  };
  useEffect(() => {
    setLiveCands(null); setSalaryPicks({});
    if (!pickerFor || !recon?.recon_id) return;
    api.get(`/admin/bank-recon/${recon.recon_id}/candidates`, { params: { line_id: pickerFor } })
      .then((r) => setLiveCands(r.data?.candidates || []))
      .catch(() => setLiveCands(null));
  }, [pickerFor, recon?.recon_id]);

  const manualMatch = async (lineId, forcedOpIds) => {
    const op_ids = forcedOpIds || Object.entries(pickerSel).filter(([, v]) => v).map(([k]) => k);
    if (op_ids.length === 0) { toast.error("Sélectionnez au moins une opération."); return; }
    setManualSaving(true);
    try {
      await api.post(`/admin/bank-recon/${recon.recon_id}/manual-match`, { line_id: lineId, op_ids });
      toast.success("Ligne rapprochée manuellement");
      setPickerFor(null);
      setPickerSel({});
      setOrphanPickFor(null);
      setOrphanLine("");
      await load();
      onIntegrated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rapprochement manuel échoué");
    } finally { setManualSaving(false); }
  };

  const requalifyCharges = async (lineIds) => {
    const n = lineIds.length;
    if (!window.confirm(
      `Affecter ${n > 1 ? `ces ${n} lignes` : "cette ligne"} au paiement de la dette de charges sociales (CNSS / AMO / IR) ?\n\n`
      + "• Le montant réglera la dette la plus ancienne en premier (FIFO).\n"
      + "• Toute dépense cabinet représentant ce même paiement sera supprimée pour éviter un double comptage.",
    )) return;
    setRequalifying(true);
    try {
      const r = await api.post(`/admin/bank-recon/${recon.recon_id}/requalify-charges`, { line_ids: lineIds });
      toast.success(`${(r.data.requalified || []).length} ligne(s) affectée(s) à la dette de charges — ${r.data.settlements_applied || 0} règlement(s) appliqué(s)`);
      if ((r.data.removed_cabinet_lines || []).length > 0) {
        toast.info(`${r.data.removed_cabinet_lines.length} dépense(s) cabinet supprimée(s) (double comptage évité)`, { duration: 8000 });
      }
      (r.data.skipped || []).forEach((s) => toast.warning(s.reason));
      setReqSelected({});
      await load();
      onIntegrated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Requalification échouée");
    } finally { setRequalifying(false); }
  };


  const allLines = recon?.lines || [];
  const isRequalifiable = (ln) => !!ln.debit && !ln.adjusted
    && !(ln.match?.op_ids || []).some((o) => String(o).startsWith("liability:") || String(o).startsWith("salary:"));
  const nReqSelected = Object.values(reqSelected).filter(Boolean).length;
  const unmatchedDebits = allLines.filter((l) => l.match?.status === "unmatched" && l.debit);
  const unmatchedCredits = allLines.filter((l) => l.match?.status === "unmatched" && l.credit);
  const adjustables = allLines.filter((l) => l.match?.adjustable && !l.adjusted);
  const matchedOrIntegrated = allLines.filter((l) => l.match?.status !== "unmatched" && !(l.match?.adjustable && !l.adjusted));
  const nSelected = Object.values(selected).filter((v) => v.checked).length;
  const nAdjSelected = Object.values(adjSelected).filter(Boolean).length;
  const curYm = `${year}-${String(month).padStart(2, "0")}`;
  const overdueSalaries = recon?.overdue_salaries || [];

  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="bank-recon-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-2.5 bg-zinc-50/60 border-b border-zinc-100 flex items-center gap-2 text-left"
        data-testid="bank-recon-toggle"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Landmark size={15} className="text-zinc-600" />
        <h2 className="text-sm font-semibold text-zinc-900">Rapprochement bancaire</h2>
        {recon && (
          <>
            <StatusBadge status={recon.status} />
            <span className="text-xs font-mono text-zinc-500 ml-1 truncate">
              {recon.filename} · {recon.summary?.n_matched || 0} rapproché(s) · {(recon.summary?.n_unmatched_debit || 0) + (recon.summary?.n_unmatched_credit || 0)} non rapproché(s)
              {(recon.summary?.n_adjusted || 0) > 0 ? ` · ${recon.summary.n_adjusted} ajusté(s)` : ""}
            </span>
            {recon.pdf_drive_web_view_link && (
              <span
                role="link"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); window.open(recon.pdf_drive_web_view_link, "_blank", "noopener"); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); window.open(recon.pdf_drive_web_view_link, "_blank", "noopener"); } }}
                className="inline-flex items-center gap-1 text-[11px] text-blue-700 hover:underline ml-2 shrink-0 cursor-pointer"
                title="Relevé bancaire archivé sur Drive (Rapprochement bancaire / Relevés / AAAA)"
                data-testid="bank-recon-statement-drive-link"
              >
                <ExternalLink size={11} /> Relevé Drive
              </span>
            )}
          </>
        )}
        <span className="ml-auto" />
      </button>
      {open && (
        <div className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef} type="file" accept="application/pdf" multiple className="hidden"
              data-testid="bank-recon-file-input"
              onChange={(e) => upload(e.target.files)}
            />
            <Button
              variant="outline" disabled={uploading}
              onClick={() => fileRef.current?.click()}
              data-testid="bank-recon-upload-btn"
            >
              {uploading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Upload size={14} className="mr-2" />}
              {uploading
                ? (uploadProgress && uploadProgress.total > 1
                  ? `Analyse IA ${uploadProgress.current}/${uploadProgress.total} — ${uploadProgress.name}…`
                  : "Analyse IA en cours…")
                : recon ? "Re-uploader un relevé (PDF)" : "Uploader un ou plusieurs relevés (PDF)"}
            </Button>
            <Button variant="outline" onClick={openHistory} data-testid="bank-recon-history-btn">
              <History size={14} className="mr-2" /> Historique {year}
            </Button>
            {recon && (
              <Button variant="outline" onClick={reanalyze} disabled={reanalyzing || uploading} data-testid="bank-recon-reanalyze-btn"
                title="Relancer le rapprochement IA sur ce relevé avec les données actuelles de l'application">
                {reanalyzing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
                {reanalyzing ? "Réanalyse en cours…" : "Relancer l'analyse"}
              </Button>
            )}
            {recon && (
              <button
                type="button" onClick={removeRecon}
                className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-rose-600"
                data-testid="bank-recon-delete-btn"
              >
                <Trash2 size={12} /> Supprimer
              </button>
            )}
            {!recon && !uploading && (
              <span className="text-xs text-zinc-500">
                L'IA détecte le mois du relevé, extrait les lignes (multi-pages) puis rapproche sémantiquement chaque transaction des opérations de l'application.
              </span>
            )}
          </div>

          {recon && (
            <>
              {recon.period_start && (
                <div className="text-[11px] font-mono text-zinc-500" data-testid="bank-recon-period">
                  Période du relevé : {recon.period_start} → {recon.period_end || "?"} · {recon.bank || "—"}
                  {recon.detected_period?.source && recon.detected_period.source !== "upload_selection" ? " · mois détecté automatiquement" : ""}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <DeltaChip
                  label="Débits (sorties)" testid="bank-recon-delta-debit"
                  statement={recon.summary?.total_debit} book={recon.summary?.book_total_out}
                  delta={recon.summary?.delta_debit || 0}
                  onClick={() => setDeltaDetail("debit")}
                />
                <DeltaChip
                  label="Crédits (entrées)" testid="bank-recon-delta-credit"
                  statement={recon.summary?.total_credit} book={recon.summary?.book_total_in}
                  delta={recon.summary?.delta_credit || 0}
                  onClick={() => setDeltaDetail("credit")}
                />
              </div>
              {deltaDetail && (
                <DeltaBreakdownDialog side={deltaDetail} recon={recon} onClose={() => setDeltaDetail(null)} />
              )}

              {adjustables.length > 0 && (
                <div className="border border-indigo-300 bg-indigo-50/40 rounded-md overflow-hidden" data-testid="bank-recon-adjustables">
                  <div className="px-3 py-2 flex items-center gap-2 border-b border-indigo-200 flex-wrap">
                    <Scale size={13} className="text-indigo-700" />
                    <span className="text-xs font-semibold text-indigo-900">
                      {adjustables.length} correspondance(s) avec écart — l'écart sera imputé uniquement au Monthly Report (lignes originales inchangées)
                    </span>
                    <Button
                      size="sm" className="ml-auto h-7 text-xs" disabled={applying || nAdjSelected === 0}
                      onClick={applyAmounts} data-testid="bank-recon-apply-amounts-btn"
                    >
                      {applying ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
                      Appliquer les montants réels ({nAdjSelected})
                    </Button>
                  </div>
                  <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
                  <table className="w-full min-w-[600px] md:min-w-0 text-xs">
                    <thead>
                      <tr className="text-indigo-800/70 font-mono uppercase tracking-wider text-[10px] border-b border-indigo-100">
                        <th className="w-8" />
                        <th className="text-left px-2 py-1">Date</th>
                        <th className="text-left px-2 py-1">Opération → rapprochée avec</th>
                        <th className="text-left px-2 py-1">Type</th>
                        <th className="text-right px-2 py-1">Comptabilisé</th>
                        <th className="text-right px-2 py-1">Réel (relevé)</th>
                        <th className="text-right px-3 py-1">Écart</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-indigo-100">
                      {adjustables.map((ln) => (
                        <tr key={ln.line_id} data-testid={`bank-recon-adjust-${ln.line_id}`}>
                          <td className="px-3 py-1.5">
                            <input
                              type="checkbox" checked={!!adjSelected[ln.line_id]}
                              onChange={() => setAdjSelected((p) => ({ ...p, [ln.line_id]: !p[ln.line_id] }))}
                              data-testid={`bank-recon-adjust-check-${ln.line_id}`}
                            />
                          </td>
                          <td className="px-2 py-1.5 font-mono text-zinc-600 whitespace-nowrap">{ln.date || "—"}{ln.time ? ` ${ln.time}` : ""}</td>
                          <td className="px-2 py-1.5 text-zinc-800">
                            <div className="truncate max-w-xs">{ln.label}</div>
                            <div className="text-[10px] text-zinc-500 font-mono truncate max-w-xs">→ {ln.match?.matched_label || ln.match?.matched_ref}</div>
                            {ln.match?.reason && (
                              <div className="text-[10px] text-indigo-700/70 italic truncate max-w-md" title={ln.match.reason}>
                                IA ({Math.round((ln.match?.confidence || 0) * 100)}%) : {ln.match.reason}
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-1.5"><KindBadge kind={ln.match?.kind} /></td>
                          <td className="px-2 py-1.5 text-right font-mono text-zinc-500 whitespace-nowrap">{fmtMad(ln.match?.book_amount_mad)}</td>
                          <td className="px-2 py-1.5 text-right font-mono font-semibold whitespace-nowrap">{fmtMad(ln.match?.real_amount_mad)}</td>
                          <td className={`px-3 py-1.5 text-right font-mono font-semibold whitespace-nowrap ${(ln.match?.delta || 0) >= 0 ? "text-amber-700" : "text-emerald-700"}`}>
                            {(ln.match?.delta || 0) > 0 ? "+" : ""}{fmtMad(ln.match?.delta)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {unmatchedDebits.length > 0 && (
                <div className="border border-amber-300 bg-amber-50/50 rounded-md overflow-hidden" data-testid="bank-recon-unmatched-debits">
                  <div className="px-3 py-2 flex items-center gap-2 border-b border-amber-200">
                    <AlertTriangle size={13} className="text-amber-700" />
                    <span className="text-xs font-semibold text-amber-900">
                      {unmatchedDebits.length} dépense(s) inexistante(s) dans les comptes (frais bancaires, dépenses non déclarées)
                    </span>
                    <Button
                      size="sm" className="ml-auto h-7 text-xs" disabled={integrating || nSelected === 0}
                      onClick={integrate} data-testid="bank-recon-integrate-btn"
                    >
                      {integrating ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
                      Intégrer aux dépenses cabinet ({nSelected})
                    </Button>
                  </div>
                  <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
                  <table className="w-full min-w-[600px] md:min-w-0 text-xs">
                    <tbody className="divide-y divide-amber-100">
                      {unmatchedDebits.map((ln) => {
                        const pickerOpen = pickerFor === ln.line_id;
                        const candidates = Object.values(Object.fromEntries([
                          ...((pickerOpen && liveCands) ? liveCands : [
                            ...(recon.orphan_ops || []).filter((o) => o.direction === "OUT"),
                            ...(recon.prev_month_ops || []),
                          ]),
                          ...overdueSalaries,
                        ].map((o) => [o.op_id, o])));
                        // Iter-311 — current-month net salaries + overdue unpaid salaries of previous months.
                        const salaryCands = candidates.filter((o) => (o.kind === "salary" && String(o.op_id).endsWith(":salaire_net_mad"))
                          || (o.kind === "liability_salary" && String(o.op_id).split(":")[3] < curYm));
                        const salPicked = salaryCands.filter((o) => salaryPicks[o.op_id]);
                        const salSum = salPicked.reduce((s, o) => s + (Number(o.amount_mad) || 0), 0);
                        const salPartial = salaryPartialRefused(salPicked, ln.debit);
                        const selSum = candidates.filter((o) => pickerSel[o.op_id]).reduce((s, o) => s + (Number(o.amount_mad) || 0), 0);
                        return (
                          <React.Fragment key={ln.line_id}>
                            <tr className="hover:bg-amber-50" data-testid={`bank-recon-line-${ln.line_id}`}>
                              <td className="px-3 py-1.5 w-8">
                                <input
                                  type="checkbox"
                                  checked={!!selected[ln.line_id]?.checked}
                                  onChange={() => toggleLine(ln)}
                                  data-testid={`bank-recon-check-${ln.line_id}`}
                                />
                              </td>
                              <td className="px-2 py-1.5 font-mono text-zinc-600 whitespace-nowrap">{ln.date || "—"}{ln.time ? ` ${ln.time}` : ""}</td>
                              <td className="px-2 py-1.5 text-zinc-800">
                                {ln.label}
                                {ln.is_bank_fee && (
                                  <span className="ml-1.5 inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase bg-purple-50 text-purple-700 border border-purple-200">
                                    Frais bancaire
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 whitespace-nowrap">
                                <select
                                  value={selected[ln.line_id]?.category || ln.suggested_category || "Autre"}
                                  onChange={(e) => setSelected((p) => ({
                                    ...p, [ln.line_id]: { checked: p[ln.line_id]?.checked ?? true, category: e.target.value },
                                  }))}
                                  className="h-7 px-1 text-xs border border-zinc-200 rounded bg-white"
                                  data-testid={`bank-recon-category-${ln.line_id}`}
                                >
                                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <button
                                  type="button"
                                  onClick={() => { setPickerFor(pickerOpen ? null : ln.line_id); setPickerSel({}); }}
                                  className="ml-2 text-[11px] underline text-indigo-700 hover:text-indigo-900"
                                  data-testid={`bank-recon-manual-btn-${ln.line_id}`}
                                >
                                  {pickerOpen ? "Fermer" : "Rapprocher…"}
                                </button>
                                <button
                                  type="button"
                                  disabled={requalifying}
                                  onClick={() => requalifyCharges([ln.line_id])}
                                  title="Affecter ce débit au paiement de la dette de charges sociales cumulée (CNSS / AMO / IR)"
                                  className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border bg-orange-50 text-orange-700 border-orange-300 hover:bg-orange-100 disabled:opacity-50"
                                  data-testid={`bank-recon-requalify-unmatched-${ln.line_id}`}
                                >
                                  <HandCoins size={10} /> Dette charges
                                </button>
                              </td>
                              <td className="px-3 py-1.5 text-right font-mono font-semibold text-rose-700 whitespace-nowrap">
                                −{fmtMad(ln.debit)} MAD
                              </td>
                            </tr>
                            {pickerOpen && (
                              <tr>
                                <td colSpan={5} className="px-3 pb-2">
                                  <div className="border border-indigo-200 bg-white rounded-md overflow-hidden" data-testid={`bank-recon-manual-picker-${ln.line_id}`}>
                                    <div className="px-3 py-1.5 text-[11px] font-semibold text-indigo-900 border-b border-indigo-100">
                                      Rapprocher avec une ou plusieurs opérations non rapprochées (ex. plusieurs références NDF payées en un seul virement)
                                    </div>
                                    <div className="max-h-48 overflow-y-auto">
                                      {candidates.length === 0 && (
                                        <div className="px-3 py-3 text-xs text-zinc-400">Aucune opération sortante non rapprochée disponible.</div>
                                      )}
                                      {candidates.map((op) => (
                                        <label key={op.op_id} className="flex items-center gap-2 px-3 py-1 text-xs border-b border-zinc-50 cursor-pointer hover:bg-indigo-50/40">
                                          <input
                                            type="checkbox"
                                            checked={!!pickerSel[op.op_id]}
                                            onChange={() => setPickerSel((p) => ({ ...p, [op.op_id]: !p[op.op_id] }))}
                                            data-testid={`bank-recon-manual-op-${op.op_id}`}
                                          />
                                          <span className="font-mono text-zinc-500 whitespace-nowrap">{op.date || "—"}</span>
                                          <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border bg-purple-50 text-purple-700 border-purple-200">{op.kind}</span>
                                          {op.prev_month && (
                                            <span title={`Dépense interne du mois précédent (${op.period}) non encore rapprochée`}
                                              className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border bg-amber-50 text-amber-800 border-amber-300"
                                              data-testid={`bank-recon-manual-op-prev-${op.op_id}`}>M-1 · {op.period}</span>
                                          )}
                                          <span className="truncate flex-1">{op.party} {op.ref}</span>
                                          <span className="font-mono font-semibold whitespace-nowrap">−{fmtMad(op.amount_mad)} MAD</span>
                                        </label>
                                      ))}
                                    </div>
                                    {salaryCands.length > 0 && (
                                      <div className="px-3 py-1.5 border-t border-indigo-100 bg-white space-y-1.5" data-testid={`bank-recon-salary-block-${ln.line_id}`}>
                                        <div className="text-[11px] text-zinc-700 font-medium">C'est un salaire de (un ou plusieurs mois) :</div>
                                        <div className="flex flex-wrap gap-1.5">
                                          {salaryCands.map((o) => {
                                            const overdue = o.kind === "liability_salary";
                                            const on = !!salaryPicks[o.op_id];
                                            return (
                                              <label key={o.op_id}
                                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] cursor-pointer transition-colors ${on ? "bg-indigo-50 border-indigo-300" : "bg-white border-zinc-200 hover:border-zinc-300"}`}>
                                                <input type="checkbox" checked={on}
                                                  onChange={() => setSalaryPicks((p) => ({ ...p, [o.op_id]: !p[o.op_id] }))}
                                                  data-testid={`bank-recon-salary-opt-${o.op_id}`} />
                                                <span className="font-medium">{o.party}</span>
                                                {overdue ? (
                                                  <span className="px-1 rounded text-[10px] font-mono uppercase bg-amber-50 text-amber-800 border border-amber-300">Salaire impayé {fmtYm(o.op_id.split(":")[3])}</span>
                                                ) : (
                                                  <span className="text-zinc-500">net {fmtYm(curYm)}</span>
                                                )}
                                                <span className="font-mono">{fmtMad(o.amount_mad)} MAD</span>
                                              </label>
                                            );
                                          })}
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="text-[11px] font-mono text-zinc-600" data-testid={`bank-recon-salary-sum-${ln.line_id}`}>
                                            Sélection {fmtMad(salSum)} / Relevé {fmtMad(ln.debit)} · Écart {fmtMad((ln.debit || 0) - salSum)} MAD
                                          </span>
                                          {salPartial && (
                                            <span className="text-[11px] text-rose-700" data-testid={`bank-recon-salary-partial-${ln.line_id}`}>
                                              Virement partiel refusé : le montant doit couvrir les salaires impayés sélectionnés
                                            </span>
                                          )}
                                          <Button size="sm" variant="outline" className="h-7 text-[11px] ml-auto"
                                            disabled={salPicked.length === 0 || salPartial || manualSaving}
                                            onClick={() => manualMatch(ln.line_id, salPicked.map((o) => o.op_id))}
                                            data-testid={`bank-recon-salary-confirm-${ln.line_id}`}>
                                            Rapprocher comme salaire
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                    <div className="px-3 py-1.5 flex items-center gap-3 border-t border-indigo-100 bg-indigo-50/30">
                                      <span className="text-[11px] font-mono text-zinc-600">
                                        Sélection {fmtMad(selSum)} / Relevé {fmtMad(ln.debit)} · Écart {fmtMad((ln.debit || 0) - selSum)} MAD
                                      </span>
                                      <Button
                                        size="sm" className="ml-auto h-6 text-[11px]"
                                        disabled={manualSaving || Object.values(pickerSel).every((v) => !v)}
                                        onClick={() => manualMatch(ln.line_id)}
                                        data-testid={`bank-recon-manual-confirm-${ln.line_id}`}
                                      >
                                        {manualSaving ? <Loader2 size={11} className="mr-1 animate-spin" /> : null}
                                        Valider le rapprochement
                                      </Button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {unmatchedCredits.length > 0 && (
                <div className="border border-sky-200 bg-sky-50/50 rounded-md overflow-hidden" data-testid="bank-recon-unmatched-credits">
                  <div className="px-3 py-2 border-b border-sky-100 text-xs font-semibold text-sky-900">
                    {unmatchedCredits.length} encaissement(s) non rapproché(s) — absent(s) des règlements du mois
                  </div>
                  <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
                  <table className="w-full min-w-[600px] md:min-w-0 text-xs">
                    <tbody className="divide-y divide-sky-100">
                      {unmatchedCredits.map((ln) => (
                        <tr key={ln.line_id} data-testid={`bank-recon-credit-${ln.line_id}`}>
                          <td className="px-3 py-1.5 font-mono text-zinc-600 whitespace-nowrap">{ln.date || "—"}{ln.time ? ` ${ln.time}` : ""}</td>
                          <td className="px-2 py-1.5 text-zinc-800">{ln.label}</td>
                          <td className="px-3 py-1.5 text-right font-mono font-semibold text-emerald-700 whitespace-nowrap">
                            +{fmtMad(ln.credit)} MAD
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {((recon.orphan_ops || []).length > 0 || overdueSalaries.length > 0) && (() => {
                // Iter-271 — salary variants (net/brut/employer cost) of one
                // employee collapse into a single row: it is ONE salary.
                const orphanDisplay = [];
                const salByUser = {};
                (recon.orphan_ops || []).forEach((op) => {
                  if (op.kind !== "salary") { orphanDisplay.push({ ...op, match_op_id: op.op_id }); return; }
                  const uid = (op.op_id || "").split(":")[1];
                  if (!salByUser[uid]) {
                    salByUser[uid] = { ...op, salary_variants: [], match_op_id: op.op_id };
                    orphanDisplay.push(salByUser[uid]);
                  }
                  const key = (op.op_id || "").split(":")[2] || "";
                  const lbl = key === "salaire_net_mad" ? "net" : key === "salaire_brut_mad" ? "brut" : "coût employeur";
                  salByUser[uid].salary_variants.push({ label: lbl, amount: op.amount_mad });
                  if (key === "salaire_net_mad") salByUser[uid].match_op_id = op.op_id;
                  if (key === "cost_employer_mad") salByUser[uid].amount_mad = op.amount_mad;
                });
                // Iter-311 — overdue unpaid salaries of previous months (live).
                overdueSalaries.forEach((op) => {
                  if (!orphanDisplay.some((o) => o.op_id === op.op_id)) orphanDisplay.push({ ...op, match_op_id: op.op_id });
                });
                return (
                <div className="border border-purple-200 bg-purple-50/40 rounded-md overflow-hidden" data-testid="bank-recon-orphan-ops">
                  <div className="px-3 py-2 border-b border-purple-100 text-xs font-semibold text-purple-900">
                    {orphanDisplay.length} opération(s) de l'application sans transaction bancaire associée
                    <div className="text-[11px] font-normal text-purple-800/80 mt-0.5">
                      Éléments présents dans l'application mais qu'aucune ligne de ce relevé ne paie. Ce n'est pas forcément une erreur : lisez l'explication sous chaque ligne.
                    </div>
                  </div>
                  <div className="max-h-96 overflow-y-auto divide-y divide-purple-100 text-xs">
                    {orphanDisplay.map((op) => {
                      const opYm = op.kind === "liability_salary" ? String(op.op_id).split(":")[3] : null;
                      const overdue = opYm && opYm < curYm;
                      const pickOpen = orphanPickFor === op.op_id;
                      const lineCands = op.direction === "IN" ? unmatchedCredits : unmatchedDebits;
                      return (
                        <div key={op.op_id} className="px-3 py-2" data-testid={`bank-recon-orphan-${op.op_id}`}>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="font-mono text-zinc-600 whitespace-nowrap">{op.date || "—"}</span>
                            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border bg-white text-purple-700 border-purple-200" title={op.kind}>
                              {KIND_FR[op.kind] || op.kind}
                            </span>
                            {op.salary_variants && (
                              <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border bg-blue-50 text-blue-700 border-blue-200" data-testid={`orphan-salary-excluded-${(op.op_id || "").split(":")[1]}`}>
                                exclu du calcul
                              </span>
                            )}
                            {overdue && (
                              <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border bg-amber-50 text-amber-800 border-amber-300" data-testid={`orphan-overdue-badge-${op.op_id}`}>
                                Échu {fmtYm(opYm)}
                              </span>
                            )}
                            <span className="text-zinc-800 font-medium flex-1 min-w-[120px]">{op.party} {op.ref}</span>
                            <span className={`font-mono font-semibold whitespace-nowrap ${op.salary_variants ? "text-zinc-400 line-through" : op.direction === "IN" ? "text-emerald-700" : "text-rose-700"}`}>
                              {op.direction === "IN" ? "+" : "−"}{fmtMad(op.amount_mad)} MAD
                            </span>
                            {op.kind === "liability_charges" && (
                              <button type="button" disabled={declaring}
                                onClick={() => declareChargesUpToDate(op.amount_mad)}
                                title={`Déclarer les charges sociales à jour au ${fmtYm(curYm)} : solde le reste estimé, avec une trace (réversible depuis l'historique de la dette)`}
                                className="inline-flex items-center gap-1 h-6 px-2 rounded border border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px] font-mono uppercase hover:bg-emerald-100 transition-colors disabled:opacity-50"
                                data-testid="bank-recon-declare-charges-btn">
                                {declaring ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                                Déclarer à jour
                              </button>
                            )}
                            <button type="button"
                              onClick={() => { setOrphanPickFor(pickOpen ? null : op.op_id); setOrphanLine(""); }}
                              title="Rapprocher avec une ligne non rapprochée du relevé"
                              className={`inline-flex items-center justify-center h-6 w-6 rounded border transition-colors ${pickOpen ? "bg-indigo-100 border-indigo-300 text-indigo-800" : "bg-white border-purple-200 text-purple-700 hover:bg-purple-100"}`}
                              data-testid={`bank-recon-orphan-match-btn-${op.op_id}`}>
                              <Link2 size={12} />
                            </button>
                          </div>
                          <div className="text-[11px] text-zinc-600 mt-1 leading-snug" data-testid={`bank-recon-orphan-hint-${op.op_id}`}>
                            {orphanHint(op, opYm, curYm, unmatchedDebits.length)}
                          </div>
                          {pickOpen && (
                            <div className="mt-1.5 flex flex-wrap items-center gap-2 border border-indigo-200 bg-white rounded-md px-3 py-1.5" data-testid={`bank-recon-orphan-picker-${op.op_id}`}>
                              <span className="text-[11px] text-zinc-700 font-medium">Ligne du relevé :</span>
                              {lineCands.length === 0 ? (
                                <span className="text-[11px] text-zinc-500">Toutes les lignes du relevé sont déjà rapprochées. Si l'une d'elles correspond à cette opération, ouvrez « lignes rapprochées » ci-dessous et défaites-la d'abord.</span>
                              ) : (
                                <select value={orphanLine} onChange={(e) => setOrphanLine(e.target.value)}
                                  className="h-7 border border-zinc-200 rounded px-2 text-[11px] bg-white max-w-full"
                                  data-testid={`bank-recon-orphan-line-select-${op.op_id}`}>
                                  <option value="">— Choisir la ligne —</option>
                                  {lineCands.map((l) => {
                                    const amt = l.debit || l.credit;
                                    const refused = salaryPartialRefused([op], amt);
                                    return (
                                      <option key={l.line_id} value={l.line_id} disabled={refused}>
                                        {`${l.date || "—"} · ${(l.label || "").slice(0, 60)} · ${fmtMad(amt)} MAD${refused ? " (montant insuffisant)" : ""}`}
                                      </option>
                                    );
                                  })}
                                </select>
                              )}
                              <Button size="sm" className="h-7 text-[11px] ml-auto"
                                disabled={!orphanLine || manualSaving}
                                onClick={() => manualMatch(orphanLine, [op.match_op_id])}
                                data-testid={`bank-recon-orphan-match-confirm-${op.op_id}`}>
                                {manualSaving ? <Loader2 size={11} className="mr-1 animate-spin" /> : null}
                                Rapprocher
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })()}


              <button
                type="button"
                onClick={() => setShowMatched((s) => !s)}
                className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-700"
                data-testid="bank-recon-show-matched"
              >
                {showMatched ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <CheckCircle2 size={12} className="text-emerald-600" />
                {matchedOrIntegrated.length} ligne(s) rapprochée(s) / intégrée(s) / ajustée(s)
              </button>
              {showMatched && matchedOrIntegrated.length > 0 && (
                <>
                  {matchedOrIntegrated.some(isRequalifiable) && (
                    <div className="flex flex-wrap items-center justify-between gap-2 px-2 py-1.5 bg-orange-50/60 border border-orange-200 rounded-md">
                      <span className="text-[11px] text-orange-900">
                        Cochez les paiements de charges sociales (CNSS / AMO / IR) puis requalifiez-les en une fois.
                      </span>
                      <Button
                        size="sm"
                        disabled={requalifying || nReqSelected === 0}
                        onClick={() => requalifyCharges(Object.entries(reqSelected).filter(([, v]) => v).map(([k]) => k))}
                        className="h-6 px-2 text-[11px] bg-orange-600 hover:bg-orange-700 text-white"
                        data-testid="bank-recon-requalify-bulk-btn"
                      >
                        {requalifying ? <Loader2 size={11} className="mr-1 animate-spin" /> : <HandCoins size={11} className="mr-1" />}
                        Requalifier la sélection ({nReqSelected}) → Dette charges
                      </Button>
                    </div>
                  )}
                <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
                <table className="w-full min-w-[600px] md:min-w-0 text-xs border border-zinc-100 rounded" data-testid="bank-recon-matched-table">
                  <tbody className="divide-y divide-zinc-100">
                    {matchedOrIntegrated.map((ln) => (
                      <tr key={ln.line_id} className="text-zinc-600">
                        <td className="px-2 py-1 w-6">
                          {isRequalifiable(ln) && (
                            <input
                              type="checkbox"
                              title="Sélectionner pour requalification groupée en dette de charges"
                              checked={!!reqSelected[ln.line_id]}
                              onChange={() => setReqSelected((p) => ({ ...p, [ln.line_id]: !p[ln.line_id] }))}
                              data-testid={`bank-recon-requalify-check-${ln.line_id}`}
                            />
                          )}
                        </td>
                        <td className="px-3 py-1 font-mono whitespace-nowrap">{ln.date || "—"}</td>
                        <td className="px-2 py-1 truncate max-w-xs">{ln.label}</td>
                        <td className="px-2 py-1">
                          {ln.adjusted ? (
                            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border bg-indigo-50 text-indigo-700 border-indigo-200" title={ln.match?.reason || ""}>
                              Écart imputé au Monthly Report ({(ln.match?.delta || 0) > 0 ? "+" : ""}{fmtMad(ln.match?.delta)})
                            </span>
                          ) : ln.match?.status === "integrated" ? (
                            <span className="inline-flex items-center gap-1">
                              <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border bg-blue-50 text-blue-700 border-blue-200">Intégrée</span>
                              <button type="button" title="Défaire l'intégration pour rapprocher la ligne manuellement (salaire, charges…)"
                                data-testid={`bank-recon-unintegrate-${ln.line_id}`}
                                onClick={async () => {
                                  if (!window.confirm("Défaire l'intégration ? La dépense cabinet créée sera supprimée et la ligne redeviendra à rapprocher.")) return;
                                  try {
                                    await api.post(`/admin/bank-recon/${recon.recon_id}/lines/${ln.line_id}/unintegrate`);
                                    toast.success("Intégration défaite — la ligne est à rapprocher");
                                    await load();
                                    onIntegrated?.();
                                  } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
                                }}
                                className="px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border border-zinc-200 text-zinc-600 hover:bg-zinc-100 transition-colors">
                                Défaire
                              </button>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1" title={ln.match?.reason || ""}>
                              <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border bg-emerald-50 text-emerald-700 border-emerald-200">
                                Rapprochée · {ln.match?.matched_ref || ln.match?.matched_type || ""}
                              </span>
                              {ln.match?.manual && (
                                <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border bg-teal-50 text-teal-700 border-teal-200">Manuel</span>
                              )}
                              {salaryMonths(ln.match, curYm).map((ym) => (
                                <span key={ym} data-testid={`bank-recon-salary-month-${ln.line_id}-${ym}`}
                                  title={ym < curYm ? "Salaire d'un mois précédent (arriéré) réglé par ce virement" : "Salaire du mois réglé par ce virement"}
                                  className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border ${ym < curYm ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                                  Salaire {fmtYm(ym)}{ym < curYm ? " · arriéré" : ""}
                                </span>
                              ))}
                              <KindBadge kind={ln.match?.kind} />
                              {!ln.adjusted && (
                                <button type="button" title="Défaire ce rapprochement : la ligne et l'opération redeviennent à rapprocher"
                                  data-testid={`bank-recon-unmatch-${ln.line_id}`}
                                  onClick={async () => {
                                    if (!window.confirm("Défaire ce rapprochement ? La ligne du relevé redeviendra « à rapprocher » et les dettes réglées par elle redeviendront dues.")) return;
                                    try {
                                      await api.post(`/admin/bank-recon/${recon.recon_id}/lines/${ln.line_id}/unmatch`);
                                      toast.success("Rapprochement défait — la ligne est à rapprocher");
                                      await load();
                                      onIntegrated?.();
                                    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
                                  }}
                                  className="px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border border-zinc-200 text-zinc-600 hover:bg-zinc-100 transition-colors">
                                  Défaire
                                </button>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1 text-right font-mono whitespace-nowrap">
                          {ln.debit ? `−${fmtMad(ln.debit)}` : `+${fmtMad(ln.credit)}`} MAD
                        </td>
                        <td className="px-2 py-1 text-right whitespace-nowrap">
                          {isRequalifiable(ln) && (
                            <button
                              type="button"
                              disabled={requalifying}
                              onClick={() => requalifyCharges([ln.line_id])}
                              title="Requalifier en paiement de charges sociales : règle la dette cumulée (CNSS / AMO / IR) et supprime la dépense cabinet correspondante"
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border bg-orange-50 text-orange-700 border-orange-300 hover:bg-orange-100 disabled:opacity-50"
                              data-testid={`bank-recon-requalify-${ln.line_id}`}
                            >
                              <HandCoins size={10} /> Dette charges
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      <Dialog open={historyOpen} onOpenChange={(o) => !o && setHistoryOpen(false)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bank-recon-history-modal">
          <DialogHeader>
            <DialogTitle>Historique des rapprochements — {history?.year || year}</DialogTitle>
          </DialogHeader>
          {(history?.items || []).length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">Aucun relevé uploadé cette année.</div>
          ) : (
            <div className="space-y-1.5">
              {(history?.items || []).map((it) => (
                <div
                  key={it.recon_id}
                  className={`border rounded-md px-3 py-2 text-xs ${it.superseded ? "border-zinc-100 opacity-60" : "border-zinc-200"}`}
                  data-testid={`bank-recon-history-item-${it.recon_id}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-zinc-900">{MONTH_FR[(it.month || 1) - 1]} {it.year}</span>
                    <StatusBadge status={it.status} />
                    {it.superseded && (
                      <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border bg-zinc-50 text-zinc-500 border-zinc-200">Remplacé</span>
                    )}
                    <span className="ml-auto font-mono text-zinc-400">{String(it.uploaded_at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-zinc-600 mt-1 font-mono truncate">{it.filename} · {it.bank || "—"} · par {it.uploaded_by_name || "—"}</div>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 font-mono text-[11px] text-zinc-600">
                    <span>{it.summary?.n_lines || 0} ligne(s)</span>
                    <span className="text-emerald-700">{it.summary?.n_matched || 0} rapprochée(s)</span>
                    <span className="text-blue-700">{it.summary?.n_integrated || 0} intégrée(s)</span>
                    <span className="text-indigo-700">{it.summary?.n_adjusted || 0} ajustée(s)</span>
                    <span className="text-amber-700">{(it.summary?.n_unmatched_debit || 0) + (it.summary?.n_unmatched_credit || 0)} non rapprochée(s)</span>
                    <span>Delta débits {fmtMad(it.summary?.delta_debit)} · crédits {fmtMad(it.summary?.delta_credit)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
